import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { garantirUsuario } from '@/lib/usuario';
import { guardarDestino } from '@/lib/destino';
import EmptyState from '@/components/ui/EmptyState';
import Accordion from '@/components/ui/Accordion';
import CartaoCarteira from './components/CartaoCarteira';
import {
  carteirasDoAparelho,
  montarCarteiras,
  type CarteiraDaConta,
  type CarteiraLinha,
} from './utils';

// "Minhas fichas": um botão grande por evento. Tocar abre a carteira.
export default function MinhasFichasPage() {
  const navigate = useNavigate();
  const { user, pronto } = useAuth();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [proximos, setProximos] = useState<CarteiraLinha[]>([]);
  const [anteriores, setAnteriores] = useState<CarteiraLinha[]>([]);
  const [reload, setReload] = useState(0);

  const voltarAoEntrar = useCallback(() => {
    guardarDestino();
    navigate(`/entrar?voltar=${encodeURIComponent('/minhas-fichas')}`, { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!pronto) return;
    let ativo = true;

    async function carregar() {
      setLoading(true);
      setErro(null);
      try {
        let daConta: CarteiraDaConta[] = [];
        if (user) {
          await garantirUsuario();
          const { data, error } = await supabase.rpc('minhas_carteiras');
          if (error) throw error;
          daConta = (data as CarteiraDaConta[]) || [];
        }

        const doAparelho = await carteirasDoAparelho(new Set(daConta.map((c) => c.evento_id)));
        const listas = montarCarteiras(daConta, doAparelho);
        if (!ativo) return;
        setProximos(listas.proximos);
        setAnteriores(listas.anteriores);
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar suas fichas. Tente novamente.'));
        }
      } finally {
        if (ativo) setLoading(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [reload, pronto, user]);

  const vazio = proximos.length === 0 && anteriores.length === 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[720px] px-5 py-6">
          <div className="h-8 w-44 animate-pulse rounded-xl bg-background-200/70" />
          <div className="mt-2 h-4 w-64 animate-pulse rounded-xl bg-background-200/70" />
          <div className="mt-6 space-y-3">
            <div className="h-[96px] w-full animate-pulse rounded-[18px] bg-background-200/70" />
            <div className="h-[96px] w-full animate-pulse rounded-[18px] bg-background-200/70" />
          </div>
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] rounded-[18px] bg-background-50 p-6 text-center shadow-pcard">
          <span className="mx-auto flex h-14 w-14 items-center justify-center text-foreground-300">
            <i className="ri-error-warning-line text-[36px]" aria-hidden />
          </span>
          <h1 className="mt-4 text-[20px] font-extrabold text-foreground-950">
            Não foi possível carregar
          </h1>
          <p className="mt-2 text-[14px] text-foreground-600">{erro}</p>
          <button
            type="button"
            onClick={() => setReload((r) => r + 1)}
            className="aperta mt-6 h-14 w-full cursor-pointer whitespace-nowrap rounded-[18px] bg-primary-500 text-[15px] font-bold text-white"
          >
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      <div className="mx-auto w-full max-w-[720px] px-5 py-6">
        <h1 className="text-[22px] font-extrabold text-foreground-950">Minhas fichas</h1>
        <p className="mt-1 text-[14px] text-foreground-600">
          Cada evento tem a sua carteira. Toque para abrir e retirar na barraca.
        </p>

        {!user && (
          <button
            type="button"
            onClick={voltarAoEntrar}
            className="aperta mt-4 flex w-full cursor-pointer items-center gap-3 rounded-[18px] bg-accent-100 px-4 py-3 text-left text-[14px] font-semibold text-accent-900"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-user-3-line" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">Entre para ver as fichas da sua conta</span>
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-s-line text-lg" aria-hidden />
            </span>
          </button>
        )}

        {vazio ? (
          <div className="mt-6">
            <EmptyState
              icone="ri-coupon-3-line"
              titulo="Você ainda não tem fichas"
              descricao="Quando você comprar fichas em um evento, ele aparece aqui com o que falta retirar."
              acao={
                <Link
                  to="/"
                  className="aperta inline-flex h-12 items-center whitespace-nowrap rounded-[18px] border border-background-200 bg-background-50 px-5 text-[14px] font-semibold text-foreground-800"
                >
                  Ver eventos
                </Link>
              }
            />
          </div>
        ) : (
          <>
            {proximos.length > 0 && (
              <section className="mt-6">
                <h2 className="text-[12px] font-bold uppercase tracking-wide text-foreground-400">
                  Próximos
                </h2>
                <div className="mt-2 space-y-3">
                  {proximos.map((carteira) => (
                    <CartaoCarteira key={carteira.codigo} carteira={carteira} />
                  ))}
                </div>
              </section>
            )}

            {anteriores.length > 0 && (
              <section className="mt-6">
                <Accordion titulo="Anteriores" resumo={`${anteriores.length}`}>
                  <div className="space-y-3">
                    {anteriores.map((carteira) => (
                      <CartaoCarteira key={carteira.codigo} carteira={carteira} />
                    ))}
                  </div>
                </Accordion>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}